import { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import ICONS from "../../../../assets/Icons/index.js";
import { FieldForceTxnDeniedApproval } from "../../../../redux/features/fieldForceTransaction/index.js";
import {
  GlobalConfiguration,
  IntervalConfiguration,
  RejectReasonLength,
} from "../../../../utils/configurationProvider.js";
import TEXT from "../../../../utils/globalText";
import SmallPopupWithInput from "../../CommonSmallPopupWithInput";
import CommonToast from "../../CommonToast/index.jsx";
import SmallPopupWithBulletPoints from "../../CommonSmallPopupWithBulletPoint/index.jsx";

const FieldForceTxnDeclinePopup = ({
  handleClose,
  txnId,
  allTxnData,
  updateAllTxn,
}) => {
  const services = useSelector((state) => state.refreshToken.services);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const isSubmittingRef = useRef(false);

  const [declineReason, setDeclineReason] = useState("");
  const [showLoader, setShowLoader] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  const handleInputChange = (event) => {
    let value = event?.target?.value ?? "";

    value = value.replace(/[^a-zA-Z0-9\s]/g, "");
    value = value.replace(/^\s+/, "");
    value = value.replace(/\s{2,}/g, " ");
    value = value.slice(
      GlobalConfiguration.lengthCheckZero,
      RejectReasonLength.lengthOfDecline,
    );

    setDeclineReason(value);
  };

  const finalize = () => {
    setShowLoader(false);
    setDeclineReason("");
    handleClose();
  };

  const showLoadingToast = (txt) => {
    CommonToast({
      text: txt,
      icon: ICONS?.LOADER_GIF,
      variant: "var(--processing)",
      buttonNeeded: false,
      btnProps: {
        enabledBgColor: "var(--primary-bg)",
        enabledTextColor: "var(--text-primary)",
        btnText: "",
        handleFunction: () => {},
        icon: "",
        btnType: "button",
        isDisabled: false,
        hoverOption: "white",
      },
    });
  };

  const getErrorMessage = (payload) => {
    const baseError =
      payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero];

    if (baseError?.errors?.[GlobalConfiguration.lengthCheckZero]) {
      return baseError.errors[GlobalConfiguration.lengthCheckZero];
    }

    if (typeof baseError === "string") {
      return baseError;
    }

    return "Error occurred";
  };

  const handleReject = async (id, reason) => {
    if (isSubmittingRef.current) return;
    isSubmittingRef.current = true;

    try {
      if (!services?.includes("AFieldForceTxn")) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "AFieldForceTxn" },
        });
        return finalize();
      }

      if (!sessionStorage.getItem("q2p_token")) {
        return finalize();
      }

      setShowLoader(true);
      showLoadingToast("Rejecting transaction.");

      const result = await dispatch(
        FieldForceTxnDeniedApproval({
          id,
          AccountIdData: reason,
        }),
      );

      const payload = result?.payload;

      await new Promise((resolve) =>
        setTimeout(resolve, IntervalConfiguration.intervalExtraLarge),
      );

      if (
        payload?.errors?.user_unauthorized?.[
          GlobalConfiguration.lengthCheckZero
        ]
      ) {
        navigate("/feature_not_assigned", {
          state: { unavailableService: "AFieldForceTxn" },
        });
        return finalize();
      }

      const updatedData = allTxnData.map((item) => {
        if (item.txn_id !== id) return item;

        const tx = payload?.transactions?.[GlobalConfiguration.lengthCheckZero];
        let lifecycleStatus = "error";
        let errorMessage = "";

        if (payload?.status === true) {
          lifecycleStatus =
            tx?.lifecycle_status === "rejected"
              ? "rejected"
              : tx?.lifecycle_status === "pending"
                ? "pendingOne"
                : "error";
        }

        if (
          payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero]?.errors
        ) {
          errorMessage =
            payload.errors.base[GlobalConfiguration.lengthCheckZero].errors[
              GlobalConfiguration.lengthCheckZero
            ];
        } else if (
          payload?.errors?.base?.[GlobalConfiguration.lengthCheckZero]
        ) {
          const base = payload.errors.base[GlobalConfiguration.lengthCheckZero];
          errorMessage = typeof base === "string" ? base : "";
        }

        return {
          ...item,
          lifecycle_status:
            item.lifecycle_status === "pending" && payload?.status === true
              ? lifecycleStatus
              : "error",
          rejected: true,
          rejection_reason: reason,
          error: payload?.status === false ? "true" : "false",
          message: payload?.status === false ? errorMessage : "",
        };
      });

      updateAllTxn(updatedData);

      if (payload?.status === false) {
        CommonToast({
          text: getErrorMessage(payload),
          icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
          variant: "var(--error)",
          buttonNeeded: false,
        });
      } else {
        CommonToast({
          text: "Transaction rejected successfully.",
          icon: ICONS?.SELECT_ALL_WITH_WHITE_FILLED_CIRCLE,
          variant: "var(--success)",
          buttonNeeded: false,
        });
      }

      finalize();
    } catch (error) {
      console.error("Reject txn failed:", error);
      toast.dismiss();
      CommonToast({
        text: "Something went wrong. Please try again.",
        icon: ICONS?.ERROR_WITH_WHITE_CIRCLE,
        variant: "var(--error)",
        buttonNeeded: false,
      });
      finalize();
    } finally {
      isSubmittingRef.current = false;
    }
  };

  useEffect(() => {
    const handleKeyDown = (event) => {
      const emojiRegex =
        /(\u00a9|\u00ae|[\u2000-\u3300]|\ud83c[\ud000-\udfff]|\ud83d[\ud000-\udfff]|\ud83e[\ud000-\udfff])/g;

      if (emojiRegex.test(event.key)) {
        event.preventDefault();
        return;
      }

      if (event.key === "Enter") {
        event.preventDefault();
        event.stopPropagation();

        if (
          !showLoader &&
          declineReason.trim().length >=
            GlobalConfiguration.rejectReasonMinLength
        ) {
          handleReject(txnId, declineReason);
        }
      }

      if (event.key === "ArrowUp" || event.key === "ArrowDown") {
        event.preventDefault();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [showLoader, declineReason, txnId]);

  const computeVariant = (value, focused) => {
    const length = (value ?? "").trim().length;

    if (length === 0) return "default";

    if (length >= GlobalConfiguration.rejectReasonMinLength) {
      return "verified";
    }

    return focused ? "typing" : "error";
  };

  return (
    <div className="fixed top-top-base left-left-base w-full-width h-full-height z-z-index-modal bg-PopUp-BackDrop bg-opacity-dark-opacity">
      <SmallPopupWithBulletPoints
        headerTitle={TEXT?.REJECT_REASON_TXN_TITLE}
        headerFontWeight="semibold"
        headerFontColor="text-primary"
        textSize="font-md"
        subtext={TEXT?.PROVIDE_REJECT_REQUEST_REASON_PROMPT}
        noteText={TEXT?.MINIMUM_TEN_CHARACTER_REQUIRED_MGS}
        isTextInputField={true}
        inputPlaceholder="Reason"
        inputAdornment={false}
        subtextwithbullet="Y"
        bulletPoints={[
          "Transaction will be rejected, and distribution will not be initiated.",
          "Transaction cannot be edited.",
        ]}
        inputVariant={computeVariant(declineReason, isFocused)}
        inputBgColor="var(--primary-bg)"
        inputFgColor="var(--text-secondary)"
        inputOnInput={(e) => {
          if (!showLoader) {
            handleInputChange(e);
          }
        }}
        inputValue={declineReason}
        inputFontKind="big"
        inputIsFullWidth={true}
        btnOneText={TEXT.CANCEL}
        btnOneHandleFunction={() => {
          if (!showLoader) {
            finalize();
          }
        }}
        btnOneEnabledBgColor="var(--primary-bg)"
        btnOneOutLineColor="var(--text-primary)"
        btnOneEnabledTextColor="var(--text-primary)"
        btnOneHoverOption="white"
        btnOneIsDisabled={showLoader}
        btnTwoText={TEXT.REJECT}
        btnTwoHandleFunction={() => {
          if (
            !declineReason ||
            declineReason.length < GlobalConfiguration?.rejectReasonMinLength ||
            showLoader
          ) {
            return;
          }
          handleReject(txnId, declineReason);
        }}
        btnTwoEnabledBgColor="var(--negative-act)"
        btnTwoEnabledTextColor="var(--text-primary)"
        btnTwoIsDisabled={
          !declineReason ||
          declineReason.length < GlobalConfiguration?.rejectReasonMinLength
        }
        btnTwoDisabledBgColor="var(--text-disabled)"
        btnTwoIsLoaderNeeded={showLoader ? true : false}
        btnTwoIsLoading={showLoader ? true : false}
        btnTwoHoverOption="black"
        btnTwoInActiveVariantNeeded={true}
      />
    </div>
  );
};

export default FieldForceTxnDeclinePopup;
